import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Icon from '../../../../../../atoms/Icon'

const useStyles = makeStyles(() => ({
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    border: 'none',
    background: 'linear-gradient(135deg, var(--color-ai-500), var(--color-ai-700))',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    '&:hover': {
      transform: 'scale(1.15)',
      boxShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
      filter: 'brightness(1.1)'
    },
    '&:active': {
      transform: 'scale(1.04)'
    }
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--border-radius-lg)',
    width: '70vw',
    maxWidth: '800px',
    height: '75vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  headerTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: 'var(--color-neutral-900)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  topicBadge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    fontFamily: 'var(--font-mono, monospace)'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    padding: '4px',
    '&:hover': {
      color: 'var(--color-neutral-600)'
    }
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: '16px 20px',
    fontSize: '13px',
    lineHeight: 1.7,
    color: 'var(--color-neutral-800)',
    '& h1, & h2, & h3': {
      marginTop: '16px',
      marginBottom: '8px',
      color: 'var(--color-neutral-900)'
    },
    '& h1': { fontSize: '18px' },
    '& h2': { fontSize: '15px' },
    '& h3': { fontSize: '14px' },
    '& pre': {
      backgroundColor: 'var(--color-neutral-050)',
      padding: '10px 14px',
      borderRadius: '6px',
      overflow: 'auto',
      fontSize: '12px',
      border: '1px solid var(--color-neutral-200)'
    },
    '& code': {
      backgroundColor: 'var(--color-neutral-050)',
      padding: '1px 4px',
      borderRadius: '3px',
      fontSize: '12px'
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12px',
      marginBottom: '12px'
    },
    '& th, & td': {
      padding: '6px 10px',
      border: '1px solid var(--color-neutral-200)',
      textAlign: 'left'
    },
    '& th': {
      backgroundColor: 'var(--color-neutral-050)',
      fontWeight: 600
    },
    '& p': {
      margin: '6px 0'
    },
    '& ul, & ol': {
      paddingLeft: '20px',
      margin: '6px 0'
    }
  },
  noContent: {
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic',
    padding: '24px',
    textAlign: 'center'
  }
}))

function DocLookupChip ({ topic, content }) {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const displayTopic = topic || 'table of contents'

  return (
    <>
      <span
        className={classes.chip}
        title={displayTopic}
        onClick={() => setOpen(true)}
      >
        <Icon name='book' customSize='14px' color='white' />
      </span>
      {open && (
        <div
          className={classes.overlay}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
        >
          <div className={classes.modal}>
            <div className={classes.header}>
              <div className={classes.headerTitle}>
                <Icon name='book' customSize='16px' />
                <span>Documentation</span>
                <span className={classes.topicBadge}>{displayTopic}</span>
              </div>
              <button
                className={classes.closeBtn}
                onClick={() => setOpen(false)}
                aria-label='Close documentation'
              >
                &times;
              </button>
            </div>
            <div className={classes.body}>
              {content
                ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                : <div className={classes.noContent}>No content available</div>}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

DocLookupChip.propTypes = {
  topic: PropTypes.string,
  content: PropTypes.string
}

export default DocLookupChip
